// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

/**
 * The two code shapes `POST /mfa/verify` takes, and how a typed code is put into
 * the form the server compares.
 *
 * One definition for both screens that take a code from a person already holding
 * an authenticator: the login form's second step and `MfaStepUpPage`. The login
 * form had its own digits-only field, so a backup code could not be typed at the
 * one prompt the help article sends a person who has lost their device to.
 *
 * Enrolment confirmation is not one of these screens: `POST /mfa/confirm` takes a
 * TOTP code only, so those inputs stay six digits.
 *
 * An input that takes one of these codes carries no `maxLength`. The browser would
 * cut a pasted code before `normaliseMfaCode` trims it, so a code copied with a
 * space in front loses its last character and can never match. The shape check is
 * what gates the submit.
 */

const TOTP_SHAPE = /^[0-9]{6}$/;
const BACKUP_CODE_SHAPE = /^[0-9a-f]{4}-[0-9a-f]{4}$/;

/**
 * Trim, and lower-case. Backup codes are issued in lower case and stored as
 * hashes, so a code typed in capitals would fail the comparison and spend one of
 * the verification attempts. A TOTP code has no letters to change.
 */
export function normaliseMfaCode(raw: string): string {
  return raw.trim().toLowerCase();
}

/** Whether a NORMALISED code has either shape, so it is worth sending. */
export function isMfaCodeShape(code: string): boolean {
  return TOTP_SHAPE.test(code) || BACKUP_CODE_SHAPE.test(code);
}
